/**
 * Per-token persistence for the share-token session credential.
 *
 * Dispatch issues a `sessionId` alongside every granted share token. From the
 * Bound tier onward that id is the credential that proves *this browser* is the
 * one that claimed the link — so it has to outlive a page reload, a back
 * button, and the self-embedded demo iframes, all of which would otherwise
 * re-present the link as a stranger.
 *
 * Keyed **per token** (`forward:sts:<token>`) so opening a second share link
 * never replays the first link's session against it.
 *
 * Every access is wrapped: `localStorage` is not merely unreliable but
 * *throwing* in Safari private mode and under some cookie-blocking settings,
 * and a share narrative must still render when storage is unavailable — it
 * just degrades to today's behavior of re-issuing a session per load.
 */

const KEY_PREFIX = "forward:sts:";

function storageKey(token: string): string {
  return `${KEY_PREFIX}${token}`;
}

/**
 * The session id previously stored for `token`, or null if there is none
 * (or storage is unavailable).
 */
export function getShareTokenSession(token: string): string | null {
  if (!token) return null;
  try {
    const stored = localStorage.getItem(storageKey(token));
    return stored ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Persist the session id Dispatch returned for `token`. A falsy id is treated
 * as "no credential" and clears any stale value rather than writing an empty
 * string that would later be replayed as a session.
 */
export function setShareTokenSession(token: string, sessionId: string): void {
  if (!token) return;
  if (!sessionId) {
    clearShareTokenSession(token);
    return;
  }
  try {
    localStorage.setItem(storageKey(token), sessionId);
  } catch {
    // Storage unavailable (private mode, quota, blocked cookies). The viewer
    // still works; it just can't prove continuity across reloads.
  }
}

/** Forget the stored session for `token`, so the next load starts clean. */
export function clearShareTokenSession(token: string): void {
  if (!token) return;
  try {
    localStorage.removeItem(storageKey(token));
  } catch {
    // See setShareTokenSession.
  }
}
